import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePermissions } from '@/features/auth';
import { ApiError } from '@/shared/api/api-error';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';

import { useChannelDeletionCheck, useDeletePaymentChannel } from '../api/pricing.queries';
import type { PaymentChannel } from '../types/pricing.types';

/**
 * Delete a payment channel (countries.manage, Admin). Only a channel no order
 * ever used, with no fee change waiting, can go: orders keep a link to their
 * channel, and reports and purchase history read through it. The dialog
 * checks first and, for a channel in use, says why and points to switching it
 * off instead. Mount only while open.
 */
export function ChannelDeleteDialog({
  countryId,
  countryName,
  channel,
  onClose,
}: {
  countryId: number;
  countryName: string;
  channel: PaymentChannel;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const check = useChannelDeletionCheck(countryId, channel.id);
  const remove = useDeletePaymentChannel(countryId);
  const { can } = usePermissions();
  const data = check.data;

  const description = check.isLoading ? (
    <>Checking whether any order used {channel.name}…</>
  ) : check.error || !data ? (
    <>Could not check whether {channel.name} is in use. Close this and try again.</>
  ) : data.orderCount > 0 ? (
    <>
      <strong>{channel.name}</strong> can&apos;t be deleted:{' '}
      {data.orderCount.toLocaleString('en-US')} order{data.orderCount === 1 ? '' : 's'} used it, and
      their history and reports link to it.
      {channel.isActive
        ? ' Switch it off instead to hide it from checkout.'
        : ' It is already off.'}
    </>
  ) : data.pendingFeesApprovalId !== null ? (
    <>
      <strong>{channel.name}</strong> has a fee change waiting for approval. Withdraw it or wait for
      the decision first
      {can('approvals.read') && (
        <>
          {' '}
          (
          <Link to="/approvals?type=PaymentChannelFees" className="font-medium underline">
            open in Approvals
          </Link>
          )
        </>
      )}
      .
    </>
  ) : (
    <>
      No order ever used <strong>{channel.name}</strong>. It will be removed from {countryName}{' '}
      permanently and the other channels move up in the checkout order. Recreating it needs a
      developer. This can&apos;t be undone.
    </>
  );

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Delete ${channel.name}?`}
      description={description}
      confirmLabel="Delete"
      variant="destructive"
      isPending={remove.isPending}
      confirmDisabled={!data?.canDelete || reason.trim().length < 3}
      onConfirm={() =>
        remove.mutate({ channelId: channel.id, reason: reason.trim() }, { onSuccess: onClose })
      }
    >
      {data?.canDelete && (
        <div className="space-y-1.5">
          <Label htmlFor="channel-delete-reason" className="text-xs">
            Reason <span className="text-muted-foreground font-normal">(required)</span>
          </Label>
          <Input
            id="channel-delete-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={500}
            disabled={remove.isPending}
            className="h-8 text-xs"
          />
        </div>
      )}
      {remove.error instanceof ApiError && (
        <p role="alert" className="text-destructive text-xs">
          {remove.error.message}
        </p>
      )}
    </ConfirmDialog>
  );
}
